import axios from 'axios';
import { API_BASE_URL } from '../config';
import { optionalAuthHeaders, asErr } from '../utils/apiHelpers';

/**
 * IMPORTANT (your current architecture):
 * - React is hosted on IIS at :8081 / :8085
 * - API is hosted separately on :8080
 * - You do NOT want ARR / IIS reverse-proxy
 *
 * Therefore:
 * ✅ DEV: use relative "/api/..." so CRA setupProxy.js can forward to :8080
 * ✅ PROD: use absolute "http://172.20.21.169:8080/api/..." (API_BASE_URL)
 *
 * If you later install ARR and want IIS to proxy /api -> :8080,
 * then you can flip USE_IIS_PROXY=true.
 */

// ✅ For your setup (NO ARR, API on 8080) this MUST be false
const USE_IIS_PROXY = false;

/** Normalize a path to start with exactly one "/" */
const normalizePath = (path: string) => (path.startsWith('/') ? path : `/${path}`);

/** Remove trailing slashes from base url to avoid "//" */
const normalizeBase = (base: string) => (base || '').replace(/\/+$/, '');

/** Build the final request URL */
const getApiUrl = (path: string) => {
  const p = normalizePath(path);

  // DEV: use CRA proxy (setupProxy.js). Browser calls same-origin localhost:3000/api/*
  if (process.env.NODE_ENV === 'development') return p;

  // PROD with IIS reverse proxy (NOT your case right now)
  if (USE_IIS_PROXY) return p;

  // PROD without reverse proxy: call API directly on :8080
  const base = normalizeBase(API_BASE_URL);

  // Fail loudly if config is missing to avoid silently calling :8081
  if (!base) {
    throw new Error('API_BASE_URL is empty. Expected something like');
  }

  return `${base}${p}`;
};

// --- simple in-memory caches ---
let REGIONS_CACHE: any[] | null = null;           // [{id, name}]
const CITIES_CACHE = new Map<number, any[]>();    // regionId -> [{id, name}]
const CENTERS_CACHE = new Map<number, any[]>();   // cityId -> [{id, name}]

/** Helper: handle cases where backend returns JSON as a string */
function ensureJson(name: string, data: any) {
  if (typeof data !== 'string') return data;

  // If we accidentally got HTML (SPA page / IIS error page), fail clearly
  const head = data.slice(0, 80).toLowerCase();
  if (head.includes('<!doctype') || head.includes('<html')) {
    throw new Error(`${name} returned HTML (not JSON). Check API URL / CORS.`);
  }

  try {
    return JSON.parse(data);
  } catch {
    throw new Error(`${name} returned non-JSON (first 160 chars): ${data.slice(0, 160)}`);
  }
}

/** GET /api/geo/regions -> [{ id, name }] (PUBLIC) */
export async function fetchRegions(force = false) {
  if (!force && REGIONS_CACHE) return REGIONS_CACHE;

  try {
    const res = await axios.get(getApiUrl('/api/geo/regions'), {
      headers: optionalAuthHeaders(),
      transitional: { forcedJSONParsing: false },
    });

    const data = ensureJson('regions', res.data) || [];
    REGIONS_CACHE = data;
    return data;
  } catch (err) {
    throw asErr('Failed to load regions', err);
  }
}

/** GET /api/geo/cities?regionId=123 -> [{ id, name }] (PUBLIC) */
export async function fetchCitiesByRegion(regionId: any, force = false) {
  const key = Number(regionId);
  if (!Number.isFinite(key) || key <= 0) return [];

  if (!force && CITIES_CACHE.has(key)) return CITIES_CACHE.get(key)!;

  try {
    const res = await axios.get(getApiUrl('/api/geo/cities'), {
      params: { regionId: key },
      headers: optionalAuthHeaders(),
      transitional: { forcedJSONParsing: false },
    });

    const data = ensureJson(`cities(${key})`, res.data) || [];
    CITIES_CACHE.set(key, data);
    return data;
  } catch (err) {
    throw asErr(`Failed to load cities (regionId=${key})`, err);
  }
}

/** GET /api/geo/moiacenters?cityId=456 -> [{ id, name }] (PUBLIC) */
export async function fetchCentersByCity(cityId: any, force = false) {
  const key = Number(cityId);
  if (!Number.isFinite(key) || key <= 0) return [];

  if (!force && CENTERS_CACHE.has(key)) return CENTERS_CACHE.get(key)!;

  try {
    const res = await axios.get(getApiUrl('/api/geo/moiacenters'), {
      params: { cityId: key },
      headers: optionalAuthHeaders(),
      transitional: { forcedJSONParsing: false },
    });

    const data = ensureJson(`centers(${key})`, res.data) || [];
    CENTERS_CACHE.set(key, data);
    return data;
  } catch (err) {
    throw asErr(`Failed to load centers (cityId=${key})`, err);
  }
}
